/**
 * The navigation bar across the top of the application.
 */

import React, { useCallback, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/css';
import asleepTokens from '@amzn/meridian-tokens/base/icon/asleep';
import Icon from '@amzn/meridian/icon';
import Masthead, { MastheadTitle, MastheadMenuButton } from '@amzn/meridian/masthead';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Toggle from '@amzn/meridian/toggle';
import Responsive from '@amzn/meridian/responsive';
import { ThemeContext } from '../theme-context';
import { useUserAuth } from '../hooks/use-user-auth';
import { UserAvatar } from './user-avatar';
import mobileLogoDarkMode from '../images/prism_logo.png';
import mobileLogoLightMode from '../images/prism_logo.png';
import logoLightMode from '../images/prism_logo.png';
import logoDarkMode from '../images/prism_logo.png';

export const AppMasthead = () => {
    const navigate = useNavigate();
    const theme = useContext(ThemeContext);
    const darkMode = theme.state.darkMode;
    const { userAlias, avatarUrl, isLoading } = useUserAuth();

    const onChange = () => {
        if (darkMode) {
            theme.dispatch({ type: 'LIGHTMODE' });
        } else {
            theme.dispatch({ type: 'DARKMODE' });
        }
    };

    const logoStyles = css({
        height: '42px',
    });


    return (
        <Responsive
            query="min-width"
            props={{
                isDesktop: { default: false, '600px': true },
                // 760px is the point that the header logo is too wide and creates a xScroll
                isWideLogo: { default: false, '760px': true },
            }}
        >
            {({ isDesktop, isWideLogo }) => (
                <Masthead>
                    {/* Left: Logo and PRISM heading together */}
                    <MastheadTitle href="/" onClick={navigate}>
                        <Row spacing="300" alignmentVertical="center">
                            {/* Render the shortened logo once the view is narrowed */}
                            {isWideLogo ? (
                                <img
                                    className={logoStyles}
                                    src={!darkMode ? logoLightMode : logoDarkMode}
                                    alt="PRISM"
                                />
                            ) : (
                                <img
                                    className={logoStyles}
                                    src={!darkMode ? mobileLogoLightMode : mobileLogoDarkMode}
                                    alt="PRISM"
                                />
                            )}
                            
                            {/* PRISM heading and description - only on desktop */}
                            {isDesktop && (
                                <Column alignmentHorizontal="start" spacing="50">
                                    <Heading level={6} color="primary">PRISM</Heading>
                                    <Text type="b100" color="secondary">Predictive Insight System for Maintenance</Text>
                                </Column>
                            )}
                        </Row>
                    </MastheadTitle>

                    {/* Right: User name and controls */}
                    <Row spacing="300" alignmentVertical="center">
                        {isDesktop && (
                            <Row spacing="200" alignmentVertical="center">
                                <UserAvatar 
                                    userAlias={userAlias} 
                                    avatarUrl={avatarUrl}
                                    size={32}
                                />
                                <Text type="b200" color="secondary">
                                    {isLoading ? "Loading..." : userAlias || "User"}
                                </Text>
                            </Row>
                        )}
                        {/* TODO: update the darkMode to use the same color as RME wiki */}
                        {/* Dark mode toggle - display in header for wider resolution, otherwise in SideMenu */}
                        {/* {isDesktop && (
                            <Toggle
                                checked={darkMode}
                                onChange={onChange}
                                data-testid="dark-mode-toggle"
                                aria-label="Dark mode"
                            >
                                <Text>
                                    <Icon tokens={asleepTokens} />
                                </Text>
                            </Toggle>
                        )} */}
                    </Row>
                </Masthead>
            )}
        </Responsive>
    );
};
